import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { ConsentoMetodo, IConsentoRow } from '../../shared/models';
import { AuthService } from '../auth/auth.service';
import { SupabaseService } from '../supabase/supabase';

interface IRow {
  id: string;
  fk_participant_app_id: string;
  esito: 'acquisito' | 'revocato';
  metodo: ConsentoMetodo | null;
  online_riferimento: string | null;
  esito_date: string;
}

// Registro consenso informato (Chi_Compila_Cosa_e_Quando_Rev2_1ab §5): append-only, un cambio di
// stato è sempre una riga nuova, mai un update — vedi supabase/migrations/0023_consent_app.sql.
// La raccolta resta manuale/fuori app: qui si registra solo che è avvenuta.
@Injectable({ providedIn: 'root' })
export class ConsensoService {
  // --- private ---
  private readonly _auth: AuthService = inject(AuthService);
  private readonly _supabase: SupabaseService = inject(SupabaseService);

  // --- public ---
  public listForInterview(interviewId: string): Observable<IConsentoRow[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IRow[]>(
      client
        .from('consent_app')
        .select('id, fk_participant_app_id, esito, metodo, online_riferimento, esito_date')
        .eq('fk_interview_app_id', interviewId)
        .returns<IRow[]>(),
    ).pipe(map((rows) => rows.map((r) => this._toRow(r))));
  }

  public register(
    participantId: string,
    interviewId: string,
    metodo: ConsentoMetodo,
    onlineRiferimento: string | null,
  ): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('consent_app').insert({
        fk_participant_app_id: participantId,
        fk_interview_app_id: interviewId,
        esito: 'acquisito',
        metodo,
        online_riferimento: onlineRiferimento,
        fk_auth_users_id_cre: session.user.id,
      }),
    ).pipe(map(() => undefined));
  }

  // TODO(consenso): questo revoca solo REGISTRA l'esito 'revocato' — non esclude ancora nulla.
  // Chi_Compila_Cosa_e_Quando_Rev2_1ab §5: "senza quel consenso i dati non entrano nello studio".
  // Oggi niente filtra un rispondente revocato/mai consenziente fuori da csv-export.service.ts o
  // da qualunque altro export/dataset — l'enforcement va costruito quando richiesto esplicitamente.
  public revoke(participantId: string, interviewId: string): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('consent_app').insert({
        fk_participant_app_id: participantId,
        fk_interview_app_id: interviewId,
        esito: 'revocato',
        fk_auth_users_id_cre: session.user.id,
      }),
    ).pipe(map(() => undefined));
  }

  private _toRow(r: IRow): IConsentoRow {
    return {
      id: r.id,
      fkParticipantAppId: r.fk_participant_app_id,
      esito: r.esito,
      metodo: r.metodo,
      onlineRiferimento: r.online_riferimento,
      esitoDate: r.esito_date,
    };
  }
}
